import { useState, useEffect, useCallback } from "react";
import driverApi from "../utils/driverApi";
import toast from "react-hot-toast";

function useSimpleGet(path, enabled = true) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const { data } = await driverApi.get(path);
      setData(data);
    } catch (err) {
      toast.error(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useDriverDashboard() { return useSimpleGet("/api/driver/dashboard"); }
export function useDriverVehicle()   { return useSimpleGet("/api/driver/vehicle"); }
export function useDriverTracking()  { return useSimpleGet("/api/driver/vehicle/tracking"); }
export function useDriverHelp()      { return useSimpleGet("/api/driver/help"); }
export function useDriverDocuments() { return useSimpleGet("/api/driver/documents"); }

export function useDriverTrips() {
  const { data, loading, reload } = useSimpleGet("/api/driver/trips");
  async function respond(tripId, response) {
    await driverApi.post(`/api/driver/trips/${tripId}/respond`, { response });
    await reload();
  }
  return { trips: data || [], loading, respond, reload };
}

export function useDriverNotifications(enabled) {
  const { data, loading, reload } = useSimpleGet("/api/driver/notifications", enabled);
  const notifications = data || [];
  const unreadCount = notifications.filter(n => !n.isRead).length;
  async function markRead(id) {
    try { await driverApi.post(`/api/driver/notifications/${id}/read`); await reload(); } catch { /* best-effort */ }
  }
  async function markAllRead() {
    try { await driverApi.post("/api/driver/notifications/read-all"); await reload(); } catch { /* best-effort */ }
  }
  return { notifications, loading, unreadCount, markRead, markAllRead, reload };
}

export function useDriverProfile() {
  const { data, loading, reload } = useSimpleGet("/api/driver/profile");
  async function updateProfile(payload) {
    await driverApi.put("/api/driver/profile", payload);
    await reload();
  }
  return { profile: data, loading, updateProfile, reload };
}

export function useDriverRequests() {
  const { data, loading, reload } = useSimpleGet("/api/driver/requests");
  async function submit(type, payload) {
    const { data } = await driverApi.post(`/api/driver/requests/${type}`, payload);
    await reload();
    return data;
  }
  return { requests: data || [], loading, submit, reload };
}
